/**
 * The locales supported by Verdi clients. This list is the single source of truth —
 * the dashboard, backend, and any other client must derive their language list from
 * here rather than maintaining a parallel constant.
 */
export const LOCALES = ["en-CA", "en-US", "fr-CA", "es-MX", "ja-JP", "pt-PT", "pa-IN"] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * The locale used as the canonical English source. Catalog entries in this locale
 * (added in follow-up PRs) must be complete; entries in other locales fall back to
 * this when absent.
 */
export const DEFAULT_LOCALE = "en-CA" as const satisfies Locale;

/**
 * Narrows a stored locale string to a supported {@link Locale}, falling back to {@link DEFAULT_LOCALE}.
 * Persisted locales are plain strings, so anything reaching a catalog or a collator comes through here
 * first. i18next's own `fallbackLng` keeps unknown locales readable but does not make the value a `Locale`.
 */
export function resolveLocale(locale: string): Locale {
    return (LOCALES as readonly string[]).includes(locale) ? (locale as Locale) : DEFAULT_LOCALE;
}

/**
 * i18next namespaces owned by `verdi-common`. The `WARNINGS` namespace holds the warning
 * translation catalog; `SENSOR_NAMING` holds the sensor/unit display-name catalog (unit
 * categories and sensor element descriptors); `TIMEZONES` holds the generated IANA-zone city
 * names; `THIRD_PARTY_API` holds display copy for the third-party API integrations; `CROPS` and
 * `CROP_VARIETIES` hold crop field labels and variety names. Declared here
 * so the dashboard (and any other consumer) can wire up the namespace name without hard-coding
 * the string.
 */
export const NAMESPACES = {
    WARNINGS: "warnings",
    NOTIFICATIONS: "notifications",
    SENSOR_NAMING: "sensorNaming",
    TIMEZONES: "timezones",
    THIRD_PARTY_API: "thirdPartyApi",
    CROPS: "crops",
    CROP_VARIETIES: "cropVarieties",
} as const;
